import React from 'react';
import { surveysApi } from '../api/surveysApi';
import type { SurveyAdminRecord } from '../types';

type FilterMode = 'active' | 'inactive' | 'all';

export function useSurveyList(filterMode?: FilterMode | null): {
  surveys: SurveyAdminRecord[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => void;
} {
  const [allSurveys, setAllSurveys] = React.useState<SurveyAdminRecord[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [tick, setTick] = React.useState(0);

  // Default to 'active' for backward compatibility
  const mode = filterMode ?? 'active';

  // Apply filtering based on mode
  const surveys = React.useMemo(() => {
    switch (mode) {
      case 'active':
        return allSurveys.filter((s) => {return s.active});
      case 'inactive':
        return allSurveys.filter((s) => {return !s.active});
      case 'all':
        return allSurveys;
      default:
        return allSurveys.filter((s) => {return s.active});
    }
  }, [allSurveys, mode]);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    surveysApi
      .listSurveys()
      .then((res) => {
        if (!cancelled) setAllSurveys(res.surveys);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [tick]);

  const refresh = React.useCallback(() => {return setTick((t) => {return t + 1})}, []);

  return { surveys, isLoading, error, refresh };
}
